import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import { Trophy, Lock, Clock, CheckCircle2, XCircle, Send, MapPin, Calendar } from "lucide-react";

const ACCESS = {
  granted: { label: "Autorizado", cls: "bg-[#5AF78E]/20 text-[#5AF78E] border-[#5AF78E]/40", icon: CheckCircle2 },
  pending: { label: "Aguardando aprovação", cls: "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/40", icon: Clock },
  denied: { label: "Negado", cls: "bg-[#C9A84C]/20 text-[#C9A84C] border-[#C9A84C]/40", icon: XCircle },
  none: { label: "Sem acesso", cls: "bg-white/5 text-[var(--text-secondary)] border-white/10", icon: Lock },
};

export default function OperatorCompetitions() {
  const { refresh, setCurrent } = useCompetition();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [requesting, setRequesting] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/operator/competitions");
      setItems((prev) => {
        const newlyGranted = data.some(
          (c) => c.access === "granted" && prev.find((p) => p.id === c.id && p.access !== "granted")
        );
        if (newlyGranted) refresh();
        return data;
      });
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const sendRequest = async (cid) => {
    setBusy(true);
    setError("");
    try {
      await api.post("/operator/access-requests", { competition_id: cid, message });
      setRequesting(null);
      setMessage("");
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const open = (cid) => {
    setCurrent(cid);
    nav("/inscricoes");
  };

  const granted = items.filter((c) => c.access === "granted");

  return (
    <div data-testid="operator-competitions-page">
      <div className="mb-8">
        <div className="overline mb-2 flex items-center gap-1">
          <Trophy size={12} /> Operadora de Evento
        </div>
        <h1 className="font-display text-5xl font-black tracking-tight">MEUS CAMPEONATOS</h1>
        <div className="text-sm text-[var(--text-secondary)] mt-1">
          Você tem acesso a <b className="text-white">{granted.length}</b> campeonato(s). Solicite acesso aos demais e
          aguarde a liberação do administrador.
        </div>
      </div>

      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-4" data-testid="operator-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((c) => {
          const a = ACCESS[c.access] || ACCESS.none;
          const isGranted = c.access === "granted";
          return (
            <div
              key={c.id}
              data-testid={`op-comp-card-${c.id}`}
              className={"card p-5 flex flex-col gap-3 border-l-4 " + (isGranted ? "border-[#5AF78E]" : "border-white/10")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-display font-black text-xl leading-tight">{c.nome}</div>
                <span className={"chip border px-2 py-0.5 text-[10px] whitespace-nowrap " + a.cls} data-testid={`op-comp-status-${c.id}`}>
                  <a.icon size={11} className="inline mr-1 -mt-0.5" />
                  {a.label}
                </span>
              </div>
              <div className="text-xs text-[var(--text-secondary)] flex flex-wrap gap-3">
                <span className="flex items-center gap-1"><Calendar size={11} /> {c.data}</span>
                {c.local && <span className="flex items-center gap-1"><MapPin size={11} /> {c.local}</span>}
              </div>
              {c.access === "denied" && c.request?.reason && (
                <div className="text-xs text-[#C9A84C]">Motivo: {c.request.reason}</div>
              )}
              {c.access === "pending" && (
                <div className="text-xs text-[var(--text-secondary)]">
                  Solicitado em {new Date(c.request.created_at).toLocaleString("pt-BR")}
                </div>
              )}

              {isGranted && (
                <button data-testid={`op-comp-open-${c.id}`} className="btn-primary mt-auto" onClick={() => open(c.id)}>
                  ABRIR CAMPEONATO
                </button>
              )}
              {(c.access === "none" || c.access === "denied") && requesting !== c.id && (
                <button
                  data-testid={`op-comp-request-${c.id}`}
                  className="btn-ghost mt-auto inline-flex items-center justify-center gap-2"
                  onClick={() => { setRequesting(c.id); setMessage(""); }}
                >
                  <Send size={13} /> {c.access === "denied" ? "Solicitar novamente" : "Solicitar acesso"}
                </button>
              )}
              {requesting === c.id && (
                <div className="mt-auto space-y-2" data-testid={`op-comp-request-form-${c.id}`}>
                  <textarea
                    data-testid={`op-comp-request-msg-${c.id}`}
                    className="input-field text-sm"
                    rows={2}
                    placeholder="Mensagem para o admin (opcional)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`op-comp-request-send-${c.id}`}
                      className="btn-primary flex-1 !py-2 !text-xs"
                      disabled={busy}
                      onClick={() => sendRequest(c.id)}
                    >
                      {busy ? "Enviando..." : "ENVIAR SOLICITAÇÃO"}
                    </button>
                    <button className="btn-ghost !py-2 !text-xs" onClick={() => setRequesting(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && !error && (
          <div className="card p-8 text-center text-[var(--text-secondary)] md:col-span-2 xl:col-span-3">
            Nenhum campeonato cadastrado ainda.
          </div>
        )}
      </div>
    </div>
  );
}
